import React, { useState, useEffect } from 'react';
import { Plus, Search, X, Upload, Monitor, Edit2, Trash2 } from 'lucide-react';
import { useApiAuth } from '../../../contexts/ApiAuthContext';
import { useAuth } from '../../../hooks/useAuth';
import { toast } from 'react-hot-toast';
import { resourceService } from '..';
import { BRAND_COLORS, NEUTRAL_COLORS, SYSTEM_COLORS } from '../../../constants/brandPalette';
import ConfirmDeleteModal from '../../../components/common/ConfirmDeleteModal';

const inp = 'w-full px-3 py-2.5 rounded-xl border text-sm outline-none transition-all';
const inpStyle = {
  borderColor: SYSTEM_COLORS.border,
  backgroundColor: SYSTEM_COLORS.shellSurface,
  color: NEUTRAL_COLORS.black,
};
const Label = ({ children }) => (
  <label className="block text-xs font-semibold mb-1.5" style={{ color: SYSTEM_COLORS.textMuted }}>
    {children}
  </label>
);
const Spinner = () => (
  <div className="flex items-center justify-center h-48">
    <div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: BRAND_COLORS.primary, borderTopColor: 'transparent' }} />
  </div>
);

const statusStyle = (status) => {
  switch (status) {
    case 'available': return { backgroundColor: SYSTEM_COLORS.successTint, color: SYSTEM_COLORS.success };
    case 'in-use':    return { backgroundColor: SYSTEM_COLORS.infoTint,    color: SYSTEM_COLORS.info };
    case 'expired':   return { backgroundColor: SYSTEM_COLORS.dangerTint,  color: SYSTEM_COLORS.danger };
    default:          return { backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted };
  }
};

const defaultForm = {
  name: '', category: '', quantity: 1, licenseType: '',
  status: 'available', expiryDate: '', description: '', images: [],
};

const SoftwareSection = () => {
  const { user } = useApiAuth();
  const { hasPermission } = useAuth();
  const [software, setSoftware]               = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [searchTerm, setSearchTerm]           = useState('');
  const [filterStatus, setFilterStatus]       = useState('all');
  const [isModalOpen, setIsModalOpen]         = useState(false);
  const [images, setImages]                   = useState([]);
  const [isEditing, setIsEditing]             = useState(false);
  const [selectedSoftware, setSelectedSoftware] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [softwareToDelete, setSoftwareToDelete]   = useState(null);
  const [formData, setFormData]               = useState(defaultForm);

  useEffect(() => { loadSoftware(); }, []);

  const loadSoftware = async () => {
    try {
      setLoading(true);
      setSoftware(await resourceService.getResources('software'));
    } catch {
      toast.error('Failed to load software items');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = (e) => {
    const newImgs = Array.from(e.target.files).map(f => ({ file: f, preview: URL.createObjectURL(f) }));
    setImages(prev => [...prev, ...newImgs]);
  };

  const removeImage = (i) => {
    setImages(prev => {
      const next = [...prev];
      URL.revokeObjectURL(next[i].preview);
      next.splice(i, 1);
      return next;
    });
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setSelectedSoftware(null);
    setFormData(defaultForm);
    setImages([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const imageFiles = images.filter(i => !i.isExisting).map(i => i.file);
      const data = { ...formData, imageFiles, userId: user.id };
      if (isEditing && selectedSoftware) {
        data.images = images.filter(i => i.isExisting).map(i => i.url);
        await resourceService.updateResource('software', selectedSoftware.id, data);
        toast.success('Software updated');
      } else {
        await resourceService.addResource('software', data);
        toast.success('Software added');
      }
      closeModal();
      loadSoftware();
    } catch {
      toast.error(`Failed to ${isEditing ? 'update' : 'add'} software`);
    }
  };

  const openEdit = (item) => {
    setIsEditing(true);
    setSelectedSoftware(item);
    setFormData({ name: item.name || '', category: item.category || '', quantity: item.quantity || 1, licenseType: item.licenseType || '', status: item.statusLabel || 'available', expiryDate: item.expiryDate || '', description: item.description || '', images: item.images || [] });
    setImages((item.images || []).map(url => ({ preview: url, isExisting: true, url })));
    setIsModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await resourceService.deleteResource('software', softwareToDelete.id);
      setSoftware(prev => prev.filter(i => i.id !== softwareToDelete.id));
      toast.success('Software deleted');
      setShowDeleteConfirm(false);
      setSoftwareToDelete(null);
    } catch {
      toast.error('Failed to delete software');
    }
  };

  const filtered = software.filter(item => {
    const q = searchTerm.toLowerCase();
    const matchSearch = item.name?.toLowerCase().includes(q) || item.category?.toLowerCase().includes(q);
    const matchFilter = filterStatus === 'all' || item.statusLabel === filterStatus;
    return matchSearch && matchFilter;
  });

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" style={{ color: SYSTEM_COLORS.textSoft }} />
          <input type="text" placeholder="Search software…" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle} />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-2.5 rounded-xl border text-sm outline-none" style={inpStyle}>
          <option value="all">All Status</option>
          <option value="available">Available</option>
          <option value="in-use">In Use</option>
          <option value="expired">Expired</option>
        </select>
        {hasPermission('resources.addSoftware') && (
          <button onClick={() => setIsModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap hover:opacity-90 transition-opacity" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>
            <Plus size={16} />
            Add Software
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? <Spinner /> : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-40 text-center">
          <Monitor className="w-8 h-8 mb-3" style={{ color: SYSTEM_COLORS.border }} />
          <p className="font-semibold text-sm" style={{ color: NEUTRAL_COLORS.black }}>No software found</p>
          <p className="text-xs mt-1" style={{ color: SYSTEM_COLORS.textMuted }}>Try adjusting your search or filter</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map(item => (
            <div key={item.id} className="flex flex-col rounded-xl border shadow-md overflow-hidden" style={{ backgroundColor: SYSTEM_COLORS.shellSurface, borderColor: SYSTEM_COLORS.borderSoft }}>
              {item.images?.length > 0 ? (
                <img src={item.images[0]} alt={item.name} className="w-full h-28 object-cover" />
              ) : (
                <div className="w-full h-28 flex items-center justify-center" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted }}>
                  <Monitor className="w-8 h-8" style={{ color: SYSTEM_COLORS.border }} />
                </div>
              )}

              <div className="flex-1 flex flex-col p-3 gap-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-sm truncate" style={{ color: NEUTRAL_COLORS.black }}>{item.name}</h3>
                    <p className="text-xs truncate" style={{ color: SYSTEM_COLORS.textMuted }}>{item.category}</p>
                  </div>
                  <span className="flex-shrink-0 text-xs font-semibold px-2 py-0.5 rounded-full capitalize" style={statusStyle(item.statusLabel)}>
                    {item.statusLabel ?? '—'}
                  </span>
                </div>

                {item.description && <p className="text-xs line-clamp-2" style={{ color: SYSTEM_COLORS.textMuted }}>{item.description}</p>}

                {item.expiryDate && (
                  <div className="flex justify-between text-xs">
                    <span style={{ color: SYSTEM_COLORS.textSoft }}>Expires</span>
                    <span className="font-medium" style={{ color: NEUTRAL_COLORS.black }}>{new Date(item.expiryDate).toLocaleDateString()}</span>
                  </div>
                )}

                {hasPermission('resources.addSoftware') && (
                  <div className="flex justify-end gap-1 pt-1 border-t mt-auto" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                    <button onClick={() => openEdit(item)} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: BRAND_COLORS.primary }}>
                      <Edit2 size={13} />
                    </button>
                    <button onClick={() => { setSoftwareToDelete(item); setShowDeleteConfirm(true); }} className="p-1.5 rounded-lg hover:opacity-70 transition-opacity" style={{ color: SYSTEM_COLORS.danger }}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" style={{ backgroundColor: SYSTEM_COLORS.shellOverlay }}>
          <div className="w-full sm:max-w-xl rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col max-h-[92dvh] shadow-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurface }}>
            <div className="flex items-center justify-between px-5 py-4 border-b flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
              <div className="flex items-center gap-2.5">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg" style={{ backgroundColor: SYSTEM_COLORS.shellSurfaceMuted }}>
                  <Monitor className="w-4 h-4" style={{ color: BRAND_COLORS.primary }} />
                </div>
                <h2 className="text-base font-semibold" style={{ color: NEUTRAL_COLORS.black }}>{isEditing ? 'Edit Software' : 'Add Software'}</h2>
              </div>
              <button onClick={closeModal} className="flex items-center justify-center w-8 h-8 rounded-lg hover:opacity-70" style={{ backgroundColor: SYSTEM_COLORS.surfaceMuted, color: SYSTEM_COLORS.textMuted }}>
                <X size={16} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="overflow-y-auto flex-1">
              <div className="p-5 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Name *</Label>
                    <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} className={inp} style={inpStyle} required />
                  </div>
                  <div>
                    <Label>Category *</Label>
                    <input type="text" value={formData.category} onChange={e => setFormData({ ...formData, category: e.target.value })} className={inp} style={inpStyle} required />
                  </div>
                  <div>
                    <Label>License Type *</Label>
                    <input type="text" value={formData.licenseType} onChange={e => setFormData({ ...formData, licenseType: e.target.value })} className={inp} style={inpStyle} required />
                  </div>
                  <div>
                    <Label>Quantity *</Label>
                    <input type="number" min={1} value={formData.quantity} onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) || 1 })} className={inp} style={inpStyle} required />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })} className={inp} style={inpStyle}>
                      <option value="available">Available</option>
                      <option value="in-use">In Use</option>
                      <option value="expired">Expired</option>
                    </select>
                  </div>
                  <div>
                    <Label>Expiry Date</Label>
                    <input type="date" value={formData.expiryDate} onChange={e => setFormData({ ...formData, expiryDate: e.target.value })} className={inp} style={inpStyle} />
                  </div>
                  <div className="col-span-2">
                    <Label>Description</Label>
                    <textarea rows={2} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} className={inp} style={inpStyle} />
                  </div>
                </div>

                <div>
                  <Label>Images</Label>
                  <div className="grid grid-cols-5 gap-3 mt-1">
                    {images.map((img, i) => (
                      <div key={i} className="relative">
                        <img src={img.preview} alt="" className="w-full h-16 object-cover rounded-xl" />
                        <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 rounded-full" style={{ backgroundColor: SYSTEM_COLORS.danger, color: '#fff' }}>
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                    <label className="flex flex-col items-center justify-center h-16 rounded-xl border-2 border-dashed cursor-pointer hover:opacity-70 transition-opacity" style={{ borderColor: SYSTEM_COLORS.border }}>
                      <Upload className="w-4 h-4" style={{ color: SYSTEM_COLORS.textSoft }} />
                      <input type="file" className="hidden" accept="image/*" multiple onChange={handleImageUpload} />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t flex-shrink-0" style={{ borderColor: SYSTEM_COLORS.borderSoft }}>
                <button type="button" onClick={closeModal} className="px-4 py-2.5 rounded-xl text-sm font-semibold border hover:opacity-70" style={{ borderColor: SYSTEM_COLORS.border, color: SYSTEM_COLORS.textMuted }}>Cancel</button>
                <button type="submit" className="px-5 py-2.5 rounded-xl text-sm font-semibold hover:opacity-90" style={{ backgroundColor: BRAND_COLORS.primary, color: '#fff' }}>
                  {isEditing ? 'Update Software' : 'Add Software'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmDeleteModal
        isOpen={showDeleteConfirm}
        onClose={() => { setShowDeleteConfirm(false); setSoftwareToDelete(null); }}
        onConfirm={confirmDelete}
        title="Delete Software"
        description={softwareToDelete ? `Delete "${softwareToDelete.name}"? This cannot be undone.` : ''}
      />
    </div>
  );
};

export default SoftwareSection;
